import { useCallback, useEffect, useRef, useState } from 'react';
import { isFormDirty, normalizePhoneDigits } from './configFormValidators';

/**
 * @param {object} options
 * @param {object|null} options.initialValues
 * @param {Function} [options.validate]
 * @param {string|number} [options.entityKey] - When this changes, form re-initializes.
 *   Prefer a stable id (customer/client uuid) so parent re-renders don't reset the form.
 */
export const useConfigForm = ({ initialValues, validate, entityKey }) => {
  const [formState, setFormState] = useState(null);
  const [originalFormState, setOriginalFormState] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const formStateRef = useRef(null);
  const initializedKeyRef = useRef(null);

  useEffect(() => {
    formStateRef.current = formState;
  }, [formState]);

  // Drop stale form state as soon as the route entity changes.
  useEffect(() => {
    const key = entityKey != null ? String(entityKey) : null;
    if (initializedKeyRef.current != null && key !== initializedKeyRef.current) {
      initializedKeyRef.current = null;
      formStateRef.current = null;
      setFormState(null);
      setOriginalFormState(null);
      setErrors({});
      setTouched({});
      setSubmitAttempted(false);
    }
  }, [entityKey]);

  useEffect(() => {
    if (!initialValues) return;

    const key = entityKey != null ? String(entityKey) : null;
    // Re-init only when entity changes (or on first load). Avoid resetting on every
    // new initialValues object reference from parent re-renders.
    if (key != null && initializedKeyRef.current === key && formStateRef.current) {
      return;
    }
    initializedKeyRef.current = key;

    setFormState(initialValues);
    setOriginalFormState(initialValues);
    setErrors({});
    setTouched({});
    setSubmitAttempted(false);
  }, [initialValues, entityKey]);

  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    let newValue = value;

    if (['supportNumber', 'faxNumberInbound'].includes(name)) {
      newValue = normalizePhoneDigits(value);
    }

    setFormState((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));

    setErrors((prevErrors) => {
      if (!prevErrors[name]) return prevErrors;
      const next = { ...prevErrors };
      delete next[name];
      return next;
    });
  }, []);

  const handleCheckboxChange = useCallback((event) => {
    const { name, checked } = event.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
  }, []);

  const validateField = useCallback(
    (fieldName, stateOverride) => {
      if (!validate) return null;
      const state = stateOverride || formStateRef.current;
      if (!state) return null;
      const nextErrors = validate(state);
      return nextErrors[fieldName] || null;
    },
    [validate]
  );

  const handleBlur = useCallback(
    (event) => {
      const { name } = event.target;
      if (!name) return;

      setTouched((prev) => ({ ...prev, [name]: true }));

      if (!validate) return;
      const current = formStateRef.current;
      if (!current) return;

      const fieldError = validate(current)[name];
      setErrors((prevErrors) => {
        const next = { ...prevErrors };
        if (fieldError) {
          next[name] = fieldError;
        } else {
          delete next[name];
        }
        return next;
      });
    },
    [validate]
  );

  const resetForm = useCallback((values) => {
    setFormState(values);
    setOriginalFormState(values);
    setErrors({});
    setTouched({});
    setSubmitAttempted(false);
  }, []);

  const runValidation = useCallback(() => {
    setSubmitAttempted(true);
    const current = formStateRef.current;
    if (!validate || !current) return true;
    const nextErrors = validate(current);
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [validate]);

  const shouldShowError = useCallback(
    (fieldName) => Boolean((submitAttempted || touched[fieldName]) && errors[fieldName]),
    [errors, submitAttempted, touched]
  );

  const isDirty = isFormDirty(formState, originalFormState);

  return {
    formState,
    originalFormState,
    errors,
    touched,
    submitAttempted,
    isDirty,
    handleInputChange,
    handleCheckboxChange,
    handleBlur,
    validateField,
    shouldShowError,
    resetForm,
    runValidation,
  };
};
